import React, { useEffect, useMemo } from 'react';
import { Box, CircularProgress, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  loadTemplates,
  loadTemplateBody,
  selectTemplates,
  selectTemplatesLoading,
  selectSelectedTemplateId,
  setSelectedTemplateId,
} from '../../store/reducers/templates';
import type { AppDispatch } from '../../store';
import type { TemplateListItem } from '../../lib/interfaces';

interface TemplateSelectorProps {
  context?: Record<string, string | null>;
  onInsert: (renderedMarkdown: string) => void;
  disabled?: boolean;
  size?: 'small' | 'medium';
}

export default function TemplateSelector(props: TemplateSelectorProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const templates = useSelector(selectTemplates);
  const templatesLoading = useSelector(selectTemplatesLoading);
  const selectedTemplateId = useSelector(selectSelectedTemplateId);
  const { disabled = false, size = 'medium' } = props;

  useEffect(() => {
    dispatch(loadTemplates());
  }, [dispatch]);

  const bodyById = useMemo(() => {
    const m = new Map<string, string>();
    templates.forEach((template) => {
      if (template.bodyMarkdown) m.set(template._id, template.bodyMarkdown);
    });
    return m;
  }, [templates]);

  const renderWithContext = (md: string) =>
    String(md).replace(/\{(\w+)\}/g, (_, k) => props.context?.[k] ?? `{${k}}`);

  const handleSelectTemplate = async (tpl: TemplateListItem | null) => {
    if (!tpl) {
      dispatch(setSelectedTemplateId(null));
      props.onInsert('');
      return;
    }

    const id = tpl._id;
    dispatch(setSelectedTemplateId(id));

    let body = bodyById.get(id) ?? '';

    if (!body) {
      const action = await dispatch(loadTemplateBody(id));

      if (loadTemplateBody.fulfilled.match(action)) {
        body = action.payload.bodyMarkdown ?? '';
      } else {
        return;
      }
    }

    const rendered = renderWithContext(body);
    props.onInsert(rendered);
  };

  const value: TemplateListItem | null = selectedTemplateId
    ? (templates.find((template) => template._id === selectedTemplateId) ??
      null)
    : null;

  return (
    <Box>
      <Autocomplete<TemplateListItem, false, false, false>
        size={size}
        disabled={disabled}
        options={templates}
        loading={templatesLoading}
        getOptionLabel={(opt) => opt?.name ?? ''}
        filterOptions={(options, { inputValue }) => {
          const q = inputValue.trim().toLowerCase();
          if (!q) return options;

          return options.filter(
            (o) =>
              (o.name || '').toLowerCase().includes(q) ||
              (o.category || '').toLowerCase().includes(q)
          );
        }}
        isOptionEqualToValue={(option, val) => option._id === val._id}
        value={value}
        onChange={(_, val) => handleSelectTemplate(val)}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('template.selectTemplate') as string}
            placeholder={t('template.searchTemplateByName') as string}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {templatesLoading ? (
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(optionProps, option) => (
          <li {...optionProps} key={option._id || option.name}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <span>{option.name}</span>
              {option.category ? (
                <span style={{ opacity: 0.7 }}>– {option.category}</span>
              ) : null}
            </Box>
          </li>
        )}
        noOptionsText={t('template.noMatches') as string}
      />
    </Box>
  );
}
